<e-card2 class="game-panel-card" padding="3">
  <header class="game-panel-header">
    <div class="game-panel-header-row">
      <h3 class="game-panel-title">{{ t('humanDesign.games.gateGuessing.title') }}</h3>
      @if (!isFinished()) {
        <button
          eBtn
          type="button"
          size="sm"
          squared
          [color]="timerVisible() ? 'primary' : 'highlight'"
          icon="clock"
          [tooltip]="t(timerVisible() ? 'humanDesign.games.gateGuessing.timerHide' : 'humanDesign.games.gateGuessing.timerShow')"
          tabindex="10"
          (click)="toggleTimer()"
        ></button>
        <button
          eBtn
          type="button"
          size="sm"
          squared
          [color]="audioEnabled() ? 'primary' : 'highlight'"
          [icon]="audioEnabled() ? 'volume-high' : 'volume-xmark'"
          [tooltip]="t(audioEnabled() ? 'humanDesign.games.gateGuessing.audioOn' : 'humanDesign.games.gateGuessing.audioOff')"
          tabindex="9"
          (click)="toggleAudio()"
        ></button>
      }
    </div>
    @if (!isFinished()) {
      @if (mode() === 'memory-poort') {
        <p class="game-panel-instruction">{{ t('humanDesign.games.gateGuessing.instructionMemoryGate') }}</p>
      } @else if (mode() === 'memory-keynote') {
        <p class="game-panel-instruction">{{ t('humanDesign.games.gateGuessing.instructionMemoryKeynote') }}</p>
      } @else {
        <p class="game-panel-instruction">{{ t('humanDesign.games.gateGuessing.instructionSelect') }}</p>
      }
    }
    @if (!isFinished() && timerVisible()) {
      <p class="game-panel-timer" aria-live="polite">
        {{ t('humanDesign.games.gateGuessing.timerThisGate') }} <strong>{{ currentGateElapsedLabel() }}</strong>
        <span class="game-panel-timer-separator">·</span>
        {{ t('humanDesign.games.gateGuessing.timerTotal') }} <strong>{{ totalElapsedLabel() }}</strong>
      </p>
    }
  </header>

  @if (isFinished()) {
    <div class="game-panel-finished" [class.is-perfect]="isPerfectGame()">
      @if (isPerfectGame()) {
        <i eIcon icon="trophy" class="game-panel-finished-trophy" size="2xl"></i>
      } @else {
        <i eIcon icon="award" class="game-panel-finished-trophy" size="2xl"></i>
      }
      <p class="game-panel-finished-title">{{ finishedTitle() }}</p>
      <div class="game-panel-stars" [attr.aria-label]="t('humanDesign.games.gateGuessing.starsAria', { level: scoreLevel() ?? 0 })">
        @for (_ of starIndices(); track $index) {
          <i eIcon icon="star" class="game-panel-star is-filled"></i>
        }
        @for (_ of emptyStarIndices(); track $index) {
          <i eIcon icon="star" class="game-panel-star is-empty"></i>
        }
      </div>
      <p class="game-panel-finished-score" [class.is-perfect]="isPerfectGame()">
        {{ totalScoreLabel() }} <span class="game-panel-finished-max">/ {{ maxPossibleScoreWithTimeBonus() }}</span>
      </p>
      @if (timeBonus() > 0) {
        <p class="game-panel-finished-score-breakdown">
          {{ scoreLabel() }} {{ t('humanDesign.games.gateGuessing.scoreBase') }} <span class="game-panel-finished-score-breakdown-plus">+</span>
          <strong>{{ timeBonusLabelFormatted() }}</strong> {{ t('humanDesign.games.gateGuessing.scoreTimeBonus') }}
        </p>
      }
      <p class="game-panel-finished-time">
        <i eIcon icon="clock" class="game-panel-finished-time-icon"></i>
        {{ t('humanDesign.games.gateGuessing.finishedTotalTime') }} <strong>{{ totalElapsedLabel() }}</strong>
        @if (timeBonusLabel(); as label) {
          <span class="game-panel-finished-time-band">— {{ t(label) }}</span>
        }
      </p>
      @if (hasSlowGates() && topSlowestGates().length > 0) {
        <p class="game-panel-finished-slowest">
          {{ t('humanDesign.games.gateGuessing.slowestIntro', { count: topSlowestGates().length }) }} @for (slow of topSlowestGates(); track slow.gate; let last = $last) {
            <strong>{{ t('humanDesign.games.gateGuessing.slowestGate', { gate: slow.gate }) }}</strong> ({{ slow.elapsedLabel }})@if (!last) {<span>, </span>}
          }
        </p>
      }
    </div>
  } @else {
    <div
      class="game-panel-question"
      [class.is-keynotes]="currentQuestionMode() === 'keynotes'"
      [class.is-memory]="isMemoryMode()"
    >
      @if (!isMemoryMode()) {
        @if (currentGateKeynotes(); as keynotes) {
          <span class="game-panel-question-keynotes" aria-live="polite">
            <strong class="game-panel-question-keynote-1">{{ keynotes.keynote1 }}</strong>
            <span class="game-panel-question-keynote-separator">/</span>
            <strong class="game-panel-question-keynote-2">{{ keynotes.keynote2 }}</strong>
          </span>
        } @else {
          <span class="game-panel-question-gate" aria-live="polite">
            {{ currentGate() }}
          </span>
        }
      }

      @if (canRequestHint()) {
        <button
          eBtn
          type="button"
          size="sm"
          squared
          color="highlight"
          icon="lightbulb"
          iconColor="warning"
          [tooltip]="t('humanDesign.games.gateGuessing.hintTooltip')"
          tabindex="5"
          (click)="requestHint()"
        ></button>
      }

      @if (canSkip()) {
        <button
          eBtn
          type="button"
          size="sm"
          squared
          color="highlight"
          icon="forward"
          iconColor="primary-75"
          [tooltip]="t('humanDesign.games.gateGuessing.skipTooltip')"
          tabindex="6"
          (click)="requestSkip()"
        ></button>
      }

      @if (currentGateWrongAttemptIndices().length > 0) {
        <span class="game-panel-question-attempts" [attr.aria-label]="t('humanDesign.games.gateGuessing.wrongAttemptsAria')">
          @for (index of currentGateWrongAttemptIndices(); track index) {
            <i eIcon icon="times" class="game-panel-attempt-cross"></i>
          }
        </span>
      }
    </div>

    @if (memoryCheatSheetContext(); as ctx) {
      <!-- Goed/fout-ring + vinkje/kruisje: gedeelde hd-card-feedback (ook op de minis-inkleurmodus). -->
      <hd-card-feedback class="game-panel-memory-cheat-sheet" [feedback]="memoryFeedback()" fadeOut>
        <hd-gate-info-cheat-sheet
          [context]="ctx"
          [hideGateNumber]="memoryPoortHidesGateNumber()"
          [hideKeynotes]="memoryKeynoteHidesKeynotes()"
          [hideColors]="memoryPoortHidesColors()"
          [hideFooterCircuit]="memoryPoortHidesFooter()"
          [hideFooterChannel]="memoryPoortHidesFooter()"
        />
      </hd-card-feedback>
    }

    @if (isMemoryMode()) {
      <form class="game-panel-memory-form" (ngSubmit)="submitMemoryAnswer()">
        @if (mode() === 'memory-poort') {
          <label class="game-panel-memory-field">
            <span class="game-panel-memory-label">{{ t('humanDesign.games.gateGuessing.labelGateNumber') }}</span>
            <div class="game-panel-memory-input-row">
              <input
                #memoryGateInputEl
                type="text"
                inputmode="numeric"
                class="form-control form-control-sm game-panel-memory-input is-gate"
                [class.is-invalid]="memoryGateWrong()"
                autocomplete="off"
                spellcheck="false"
                tabindex="1"
                [ngModel]="memoryGateInput()"
                (ngModelChange)="onMemoryGateInput($event)"
                [ngModelOptions]="{ standalone: true }"
              />
            </div>
          </label>
        } @else if (mode() === 'memory-keynote') {
          <label class="game-panel-memory-field">
            <span class="game-panel-memory-label">{{ t('humanDesign.games.gateGuessing.labelKeynote1') }}</span>
            <div class="game-panel-memory-input-row">
              <input
                #memoryKeynote1InputEl
                type="text"
                class="form-control form-control-sm game-panel-memory-input"
                [class.is-invalid]="memoryKeynote1Wrong()"
                [class.is-locked]="memoryValidations().keynote1"
                [readOnly]="memoryValidations().keynote1"
                autocomplete="off"
                spellcheck="false"
                tabindex="1"
                [ngModel]="memoryKeynote1Input()"
                (ngModelChange)="onMemoryKeynote1Input($event)"
                [ngModelOptions]="{ standalone: true }"
              />
              @if (memoryValidations().keynote1) {
                <i eIcon icon="check" class="game-panel-memory-locked-icon" [attr.aria-label]="t('humanDesign.games.gateGuessing.validatedAria')"></i>
              } @else if (canValidateMemoryKeynote1()) {
                <button
                  eBtn
                  type="button"
                  size="sm"
                  squared
                  color="highlight"
                  icon="check"
                  iconColor="primary-75"
                  [tooltip]="t('humanDesign.games.gateGuessing.validateFieldTooltip')"
                  tabindex="3"
                  (click)="validateMemoryField('keynote1')"
                ></button>
              }
            </div>
            @if (memoryKeynote1AutocompleteSuggestion(); as suggestion) {
              <button
                type="button"
                class="game-panel-memory-suggestion"
                tabindex="-1"
                (click)="applyMemoryKeynote1Autocomplete()"
                [title]="t('humanDesign.games.gateGuessing.suggestionTooltip')"
              >{{ suggestion }}</button>
            }
          </label>
          @if (currentGateHasKeynote2()) {
            <label class="game-panel-memory-field">
              <span class="game-panel-memory-label">{{ t('humanDesign.games.gateGuessing.labelKeynote2') }}</span>
              <div class="game-panel-memory-input-row">
                <input
                  type="text"
                  class="form-control form-control-sm game-panel-memory-input"
                  [class.is-invalid]="memoryKeynote2Wrong()"
                  [class.is-locked]="memoryValidations().keynote2"
                  [readOnly]="memoryValidations().keynote2"
                  autocomplete="off"
                  spellcheck="false"
                  tabindex="2"
                  [ngModel]="memoryKeynote2Input()"
                  (ngModelChange)="onMemoryKeynote2Input($event)"
                  [ngModelOptions]="{ standalone: true }"
                />
                @if (memoryValidations().keynote2) {
                  <i eIcon icon="check" class="game-panel-memory-locked-icon" [attr.aria-label]="t('humanDesign.games.gateGuessing.validatedAria')"></i>
                } @else if (canValidateMemoryKeynote2()) {
                  <button
                    eBtn
                    type="button"
                    size="sm"
                    squared
                    color="highlight"
                    icon="check"
                    iconColor="primary-75"
                    [tooltip]="t('humanDesign.games.gateGuessing.validateFieldTooltip')"
                    tabindex="4"
                    (click)="validateMemoryField('keynote2')"
                  ></button>
                }
              </div>
              @if (memoryKeynote2AutocompleteSuggestion(); as suggestion) {
                <button
                  type="button"
                  class="game-panel-memory-suggestion"
                  tabindex="-1"
                  (click)="applyMemoryKeynote2Autocomplete()"
                  [title]="t('humanDesign.games.gateGuessing.suggestionTooltip')"
                >{{ suggestion }}</button>
              }
            </label>
          }
        }
        <div class="game-panel-memory-actions">
          <button
            eBtn
            type="submit"
            size="sm"
            color="primary"
            icon="check"
            tabindex="7"
          >{{ t('humanDesign.games.gateGuessing.answer') }}</button>
        </div>
      </form>
    }

    @if (!isMemoryMode() && revealedHints().length > 0) {
      <ul class="game-panel-hints" [attr.aria-label]="t('humanDesign.games.gateGuessing.hintsAria')" aria-live="polite">
        @for (slot of revealedHints(); track $index) {
          <li class="game-panel-hint" [class]="'is-kind-' + slot.kind">
            <span class="game-panel-hint-category">{{ t(slot.categoryLabel) }}:</span>
            <span class="game-panel-hint-value" [innerHTML]="slot.valueHtml"></span>
          </li>
        }
      </ul>
    }
  }

  @if (historyRows().length > 0) {
    <div class="game-panel-history-toolbar">
      <span class="game-panel-history-toolbar-label">{{ t('humanDesign.games.gateGuessing.historyTitle') }}</span>
      <input
        type="search"
        class="form-control form-control-sm game-panel-history-search"
        [value]="historySearch()"
        (input)="onHistorySearchInput($event)"
        [placeholder]="t('humanDesign.games.gateGuessing.historySearchPlaceholder')"
        [attr.aria-label]="t('humanDesign.games.gateGuessing.historySearchAria')"
        tabindex="12"
      />
      <button
        eBtn
        type="button"
        size="sm"
        squared
        [color]="showAllHintsInHistoryByDefault() ? 'primary' : 'highlight'"
        icon="eye"
        [tooltip]="t(showAllHintsInHistoryByDefault() ? 'humanDesign.games.gateGuessing.historyHintsHide' : 'humanDesign.games.gateGuessing.historyHintsShow')"
        tabindex="11"
        (click)="toggleShowAllHintsInHistoryByDefault()"
      ></button>
    </div>
    <ol class="game-panel-history" [attr.aria-label]="t('humanDesign.games.gateGuessing.historyTitle')">
      @for (row of filteredHistoryRows(); track row.key) {
        <li
          class="game-panel-history-entry"
          [class]="'is-kind-' + row.kind + ' is-group-' + row.groupPosition"
          [class.is-expandable]="isHistoryRowExpandable(row)"
          [class.is-expanded]="isHistoryRowExpanded(row)"
          [attr.role]="isHistoryRowExpandable(row) ? 'button' : null"
          [attr.tabindex]="isHistoryRowExpandable(row) ? 0 : null"
          [attr.aria-expanded]="isHistoryRowExpandable(row) ? isHistoryRowExpanded(row) : null"
          (click)="toggleHistoryRowExpansion(row)"
          (keydown)="onHistoryRowKeydown($event, row)"
        >
          <span class="game-panel-history-label">
            {{ row.label }}
            @if (row.elapsedLabel) {
              <span
                class="game-panel-history-elapsed"
                [class.is-slow]="row.elapsedIsSlow"
              >({{ row.elapsedLabel }})</span>
            }
          </span>
          <span class="game-panel-history-points" [class]="row.pointsClass">{{ row.pointsLabel }}</span>
          <i eIcon [icon]="row.icon" class="game-panel-history-icon" [class]="row.iconClass"></i>
        </li>
        @if (isHistoryRowExpanded(row)) {
          <li class="game-panel-history-hints-entry" [attr.aria-label]="t('humanDesign.games.gateGuessing.hintInfoAria')">
            <ul class="game-panel-history-hints">
              @for (slot of hintSlotsForHistoryRow(row); track $index) {
                <li class="game-panel-hint" [class]="'is-kind-' + slot.kind">
                  <span class="game-panel-hint-category">{{ t(slot.categoryLabel) }}:</span>
                  <span class="game-panel-hint-value" [innerHTML]="slot.valueHtml"></span>
                </li>
              }
            </ul>
          </li>
        }
      }
    </ol>
  }

  <footer class="game-panel-footer">
    <p class="game-panel-score">{{ t('humanDesign.games.gateGuessing.score') }}: <strong>{{ scoreLabel() }}</strong></p>
    <button
      eBtn
      type="button"
      size="sm"
      color="highlight"
      icon="undo"
      iconColor="primary-75"
      tabindex="8"
      (click)="requestRestart()"
    >{{ t('humanDesign.games.gateGuessing.restart') }}</button>
  </footer>
</e-card2>
